import { useState, type SetStateAction } from 'react';

export type SortDir = 'asc' | 'desc';

export interface RankedSortState<K extends string> {
    key: K;
    dir: SortDir;
}

export interface RankedSortColumn<T> {
    /** Value pulled from a row for comparison. Numbers sort numerically, strings by locale. */
    get: (row: T) => number | string;
    /** Direction applied the first time this column is selected (text asc, metrics desc read best). */
    initialDir: SortDir;
}

export interface RankedSort<T, K extends string> {
    /** Rows in the active sort order — sorted in full so pagination pages through sorted order. */
    sorted: T[];
    /** The active sort. */
    state: RankedSortState<K>;
    /** Select a column: toggles direction when already active, else applies its initial direction. */
    toggle: (key: K) => void;
    /** Sort an arbitrary row array by the active sort — used to order nested child rows the same way. */
    sortRows: (rows: T[]) => T[];
}

/**
 * Client-side column sort for {@link RankedList}. The whole row set is sorted, so
 * the `#` rank and pagination follow the chosen order. Clicking the active
 * column flips its direction.
 */
export function useRankedSort<T, K extends string>(rows: T[], columns: Record<K, RankedSortColumn<T>>, initial: RankedSortState<K>): RankedSort<T, K> {
    const [state, setState] = useState<RankedSortState<K>>(initial);

    const toggle = (key: K): void => {
        const next: SetStateAction<RankedSortState<K>> = (prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: columns[key].initialDir });
        setState(next);
    };

    const column = columns[state.key];
    const factor = state.dir === 'asc' ? 1 : -1;
    const sortRows = (input: T[]): T[] =>
        [...input].sort((a, b) => {
            const av = column.get(a);
            const bv = column.get(b);
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
            return String(av).localeCompare(String(bv)) * factor;
        });
    const sorted = sortRows(rows);

    return { sorted, state, toggle, sortRows };
}
